import { useCallback, useMemo } from "react";
import { type ApolloError, type FetchResult } from "@apollo/client";
import { AddressInput } from "src/types/ctgraphql.d";
import {
  UpdateCartData,
  UpdateCartProps,
  useUpdateCart,
} from "src/hooks/useUpdateCart";
import { requestSource, useSegment } from "src/hooks/useSegment";
import { storeConfig } from "src/config/app-config";
import { CartInfo } from "src/types/cartInfo";

interface UpdateCheckoutSummaryHook {
  data?: UpdateCartData;
  loading: boolean;
  error?: ApolloError;
  updateCartAddress: (
    shippingAddress: AddressInput,
    billingAddress: AddressInput,
    customerEmail: string,
    cartInfo: CartInfo
  ) => Promise<FetchResult<UpdateCartData> | undefined>;
}

function useUpdateCartAddress({
  refetchQueries = [],
  onCompleted,
  onError,
}: UpdateCartProps = {}): UpdateCheckoutSummaryHook {
  const { user } = useSegment();

  const anonymousId = useMemo(() => {
    return user?.anonymousId?.() || "";
  }, [user]);

  const { updateCart, data, loading, error } = useUpdateCart({
    refetchQueries,
    onCompleted,
    onError,
  });

  const updateCartAddress: UpdateCheckoutSummaryHook["updateCartAddress"] =
    useCallback(
      async (shippingAddress, billingAddress, customerEmail, cartInfo) => {
        return await updateCart({
          cartInfo,
          actions: [
            {
              setLocale: {
                locale: storeConfig.locale,
              },
            },
            {
              setCustomerEmail: {
                email: customerEmail,
              },
            },
            {
              setShippingAddress: {
                address: shippingAddress,
              },
            },
            {
              setBillingAddress: {
                address: billingAddress,
              },
            },
            {
              setCustomField: {
                name: "segment_anonymous_id",
                value: `\"${anonymousId}\"`,
              },
            },
            {
              setCustomField: {
                name: "device_type",
                value: `\"${requestSource.request_source}\"`,
              },
            },
          ],
        });
      },
      [updateCart, storeConfig.locale, anonymousId]
    );

  return { updateCartAddress, data, loading, error };
}

export { useUpdateCartAddress };
